import { useEffect, useState } from 'react'
import { MeasuredImage, measureImage } from '~/helpers/measureImage'

// The image with the most pixels, or undefined when none loaded.
const pickLargest = (images: (MeasuredImage | undefined)[]) => {
  let largest: MeasuredImage | undefined

  for (const image of images) {
    if (!image) {
      continue
    }

    if (
      !largest ||
      image.width * image.height > largest.width * largest.height
    ) {
      largest = image
    }
  }

  return largest
}

// Reports the aspect ratio of the image with the most pixels among `srcs`.
// Ranking the images needs their intrinsic sizes, and the CDN scales to any
// requested width, so unlike useImageAspectRatio this has to load the
// originals rather than a small probe. They are fetched once the page is idle
// so they never compete with the visible image. `srcs` is an effect
// dependency, so callers should memoise it.
export const useLargestImageAspectRatio = (srcs: string[]) => {
  const [aspectRatio, setAspectRatio] = useState<number>()

  useEffect(() => {
    if (srcs.length === 0) {
      setAspectRatio(undefined)

      return undefined
    }

    let cancelled = false

    const measure = () => {
      Promise.all(srcs.map(measureImage)).then((images) => {
        if (cancelled) {
          return
        }

        const largest = pickLargest(images)

        setAspectRatio(largest ? largest.width / largest.height : undefined)
      })
    }

    const hasIdleCallback = typeof window.requestIdleCallback === 'function'
    const handle = hasIdleCallback
      ? window.requestIdleCallback(measure)
      : window.setTimeout(measure, 0)

    return () => {
      cancelled = true

      if (hasIdleCallback) {
        window.cancelIdleCallback(handle)
      } else {
        window.clearTimeout(handle)
      }
    }
  }, [srcs])

  return aspectRatio
}
